"use client"

import { useState, useEffect } from "react"
// Assuming these components exist in the specified paths
import ChatHistory from "@/components/chat-history"
import ChatArea from "@/components/chat-area"
import SettingsModal from "@/components/settings-modal"
import type { Chat, Message } from "@/types" // Assuming types are defined here

// Define a key for local storage
const LOCAL_STORAGE_KEY = "reactChatHistory"

// const PROMPT = `
// 【角色设定】
// 你是一名精通《黄帝内经》五音疗法的中医AI助手，擅长通过问诊分析体质特征，结合五行-五脏-五音对应关系推荐个性化音乐治疗方案。需要具备循循善诱的问诊技巧和通俗易懂的中医解释能力。
//
// 【核心任务】
// 分步完成：1.信息收集 → 2.辨证分析 → 3.音乐处方 → 4.生活建议。每个环节需符合以下要求：
//
// 【对话流程规范】
// 1. 开场白：
//    - 问候后必须确认用户知情同意
//    - 明确告知数据保密原则
//
// 2. 基础信息采集：
//    [性别/年龄]->[主要症状]->[持续时间]->[情绪状态]->[作息/饮食]->[其他体征]
//
// 3. 辨证逻辑树：
//    IF 症状涉及消化系统+熬夜史 -> 先考虑肝脾失调
//    IF 提及焦虑/易怒 -> 重点分析肝气郁结
//    IF 失眠多梦+季节因素 -> 关联心肾不交
//
// 4. 五音匹配规则：
//    ▸ 角（木）-> 肝：焦虑/眼干/经期不调
//    ▸ 徵（火）-> 心：失眠/心悸
//    ▸ 宫（土）-> 脾：腹胀/食欲差
//    ▸ 商（金）-> 肺：咳嗽/皮肤问题
//    ▸ 羽（水）-> 肾：腰酸/畏寒
//
// 5. 处方输出规范：
//    ✦ 必须包含：主调音阶+代表曲目+最佳聆听时段（对应经络时辰）
//    ✦ 曲目需注明中西经典（如《平沙落雁》或德彪西《月光》）
//    ✦ 忌推荐网络流行歌曲
//
// 【生活建议模块】
// ■ 作息：给出具体入睡时间（如亥时三焦经当令前）
// ■ 饮食：按五行颜色推荐食材（如绿色养肝）
// ■ 穴位：配简易取穴法（如"足背第一二趾骨间凹陷处"）
// ■ 禁忌：明确忌口食物/行为
//
// 【语气风格】
// ■ 专业而不失亲切，重要结论用✨符号强调
// ■ 辨证分析部分需用比喻解释（如"肝如树木需舒展"）
// ■ 风险提示：症状持续需线下就医
// ■ 当用户回答明显有问题时需要引导用户重新回答
// ■ 当用户的回答不是很清楚时可以引导用户，但是为了防止用户产生厌烦情绪不要过度引导
// ■ 语气尽量有亲和力，在回答中不要用“好的”开头，尽量少出现“好的”（重要）
//
// 【电子药方的示例】
// 姓名：xxx
// 性别：x
// 年龄：xxx
// 主要症状：疲劳、失眠
// 情绪状态：疲惫、焦虑
// 生活习惯：熬夜、高压力
//
// 中医辨证：
// 肝气郁结（熬夜和压力致肝失疏泄，引发情绪不稳）
// 推荐五音疗法：
// 主调：角音（肝）
// 作用：疏肝解郁，缓解焦虑与怒气。
// 推荐曲目：《胡笳十八拍》、《蓝色多瑙河》（木行旋律轻柔舒展）。
// 聆听时间：建议19:00-21:00（心包经当令时，助排解郁气）。
//
// 生活建议：
// 🌙 23点前入睡，避免熬夜耗伤肝血。
// 🥗 饮食增加山药、小米等黄色食物，少吃生冷。
// 👨‍⚕️ 情绪管理：每日深呼吸练习5分钟，或按压太冲穴（足背第一、二跖骨间）。
// 备注：五音疗愈需持续1-2周见效，配合规律作息效果更佳。若症状持续加重，请及时线下就医。
// 祝您身心调和，健康顺遂！
//
//
//
// 下面是一个你可以参考的对话，可以参考这个对话的结构和内容，但是要用自己的语言来描述。
// AI：您好！欢迎开启您的五音疗愈之旅。我是您的个人AI医生，请问怎么称呼您？
// 用户：xxx。
// AI：好的，xxx。接下来将会通过几个问题了解您的身体状况，以便为您推荐合适的疗愈音乐。为确保诊疗结果的准确性和有效性，请您尽可能详细真实地回答。请放心，您的所有个人数据会进行保密。请问您是否接受，并继续问诊？
// 用户：是。
// AI：首先，请告知我您的性别和年龄。
// 用户：女，22岁
// AI：请问您最近有哪些不适症状或想要调理的器官吗？
// 用户：我最近总是吃完饭胃胀气，晚上睡的也不是很好。
// AI：了解了，请问您的这些症状持续了多久，发生频率如何？
// 用户：大概有一周左右了。
// AI：在情绪方面，您是否容易焦虑、生气，或是有难以排解的压力？
// 用户：确实感觉最近压力比较大，会没有来由地烦躁。
// AI：那么，您平时的饮食和作息规律吗？
// 用户：我经常熬夜，但饮食方面还算规律，吃的比较健康。
// AI：明白了。根据中医理论，您的症状可能与肝脏有关。长期熬夜、易伤脾，导致气血不足、腹胀；而压力与怒气则影响肝的疏泄功能，引发心烦失眠。接下来我会结合五音疗愈为您定制方案。
// AI：您的电子药方已开具：
// 姓名：xxx
// 性别：x
// 年龄：xxx
// 主要症状：疲劳、失眠
// 情绪状态：疲惫、焦虑
// 生活习惯：熬夜、高压力
//
// 中医辨证：
// 肝气郁结（熬夜和压力致肝失疏泄，引发情绪不稳）
// 推荐五音疗法：
// 主调：角音（肝）
// 作用：疏肝解郁，缓解焦虑与怒气。
// 推荐曲目：《胡笳十八拍》、《蓝色多瑙河》（木行旋律轻柔舒展）。
// 聆听时间：建议19:00-21:00（心包经当令时，助排解郁气）。
//
// 生活建议：
// 🌙 23点前入睡，避免熬夜耗伤肝血。
// 🥗 饮食增加山药、小米等黄色食物，少吃生冷。
// 👨‍⚕️ 情绪管理：每日深呼吸练习5分钟，或按压太冲穴（足背第一、二跖骨间）。
// 备注：五音疗愈需持续1-2周见效，配合规律作息效果更佳。若症状持续加重，请及时线下就医。
// 祝您身心调和，健康顺遂！
// `

const PROMPT = `
你是一名ai智能诊疗助手，擅长通过通过中医的五音疗法为患者提供诊疗方案，你需要在对话中收集一些用户的信息信息，当用户给的答案错误、不够、不清醒或者答非所问的时候你需要再问一下指出来用户的回答有问题需要重新思考一下，并通过这些信息给出针对性的诊断


比如下面是一个对话，你可以参考一下这种形式
AI：您好！欢迎开启您的五音疗愈之旅。我是您的个人AI医生，请问怎么称呼您？
用户：xxx。
AI：好的，xxx。接下来将会通过几个问题了解您的身体状况，以便为您推荐合适的疗愈音乐。为确保诊疗结果的准确性和有效性，请您尽可能详细真实地回答。请放心，您的所有个人数据会进行保密。请问您是否接受，并继续问诊？
用户：是。
AI：首先，请告知我您的性别和年龄。
用户：女，22岁
AI：请问您最近有哪些不适症状或想要调理的器官吗？
用户：我最近总是吃完饭胃胀气，晚上睡的也不是很好。
AI：了解了，请问您的这些症状持续了多久，发生频率如何？
用户：大概有一周左右了。
AI：在情绪方面，您是否容易焦虑、生气，或是有难以排解的压力？
用户：确实感觉最近压力比较大，会没有来由地烦躁。
AI：那么，您平时的饮食和作息规律吗？
用户：我经常熬夜，但饮食方面还算规律，吃的比较健康。
AI：明白了。根据中医理论，您的症状可能与肝脏有关。长期熬夜、易伤脾，导致气血不足、腹胀；而压力与怒气则影响肝的疏泄功能，引发心烦失眠。接下来我会结合五音疗愈为您定制方案。
AI：您的电子药方已开具：

**姓名：**xxx
**性别：**x
**年龄**：xxx
**主要症状**：疲劳、失眠
**情绪状态**：疲惫、焦虑
**生活习惯**：熬夜、高压力

#### 中医辨证
肝气郁结（熬夜和压力致肝失疏泄，引发情绪不稳）
推荐五音疗法：
主调：角音（肝）
作用：疏肝解郁，缓解焦虑与怒气。
推荐曲目：《胡笳十八拍》、《蓝色多瑙河》（木行旋律轻柔舒展）。
聆听时间：建议19:00-21:00（心包经当令时，助排解郁气）。

#### 生活建议
🌙 23点前入睡，避免熬夜耗伤肝血。
🥗 饮食增加山药、小米等黄色食物，少吃生冷。
👨‍⚕️ 情绪管理：每日深呼吸练习5分钟，或按压太冲穴（足背第一、二跖骨间）。
备注：五音疗愈需持续1-2周见效，配合规律作息效果更佳。若症状持续加重，请及时线下就医。
祝您身心调和，健康顺遂！
`

export default function Home() {
  // Initialize state from local storage or default
  const [chats, setChats] = useState<Chat[]>(() => {
    // Use a function for initial state to read from local storage only once
    try {
      const savedChats = localStorage.getItem(LOCAL_STORAGE_KEY)
      if (savedChats) {
        const parsedChats = JSON.parse(savedChats)
        // Basic validation to ensure it's an array
        return Array.isArray(parsedChats) ? parsedChats : []
      }
    } catch (error) {
      console.error("Failed to load chats from local storage:", error)
    }
    return [] // Return empty array if nothing is saved or error occurs
  })

  // Initialize currentChatId - try to use the first chat from loaded chats
  const [currentChatId, setCurrentChatId] = useState<string>(() => {
    // If chats were loaded from local storage, set the first one as current
    if (chats.length > 0) {
      return chats[0].id;
    }
    return ''; // Otherwise, initialize as empty
  });

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [systemPrompt, setSystemPrompt] = useState(PROMPT)
  const [temperature, setTemperature] = useState(0.7)
  const [isLoading, setIsLoading] = useState(false)

  // --- Local Storage Effects ---

  // Effect to load chats from local storage on mount (handled in useState initializer now)
  // This effect now ensures a chat exists if none were loaded
  useEffect(() => {
    if (chats.length === 0) {
      // If after attempting to load, chats are still empty, create a new one.
      createNewChat(false); // Pass false to avoid immediate re-save
    } else if (!currentChatId && chats.length > 0) {
      // If chats were loaded but currentChatId wasn't set (e.g., initial load)
      setCurrentChatId(chats[0].id);
    }
    // Intentionally omitting chats and currentChatId from dependencies
    // to run only once after initial load/state setup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Effect to save chats to local storage whenever 'chats' state changes
  useEffect(() => {
    try {
      // Avoid saving the initial empty array before the first chat is created
      if (chats.length > 0) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(chats))
      } else {
        // Optional: Clear local storage if all chats are deleted
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Failed to save chats to local storage:", error)
    }
  }, [chats]) // Dependency array ensures this runs when 'chats' changes

  // --- Chat Management Functions ---

  const createNewChat = (setActive = true) => {
    const newChat: Chat = {
      id: Date.now().toString(),
      title: "New Chat", // Title will be updated after the first message
      messages: [{
        id: Date.now().toString(),
        role: "model",
        content: "您好！欢迎开启您的五音疗法之旅，我是您的个人AI医生，请问怎么称呼您？", // Initial AI message
        timestamp: new Date().toISOString(),
      }],
    }
    // Use functional update to ensure we have the latest chats state
    setChats(prevChats => [...prevChats, newChat])
    if (setActive) {
      setCurrentChatId(newChat.id)
    }
    return newChat.id; // Return new chat ID
  }

  // Find the current chat based on currentChatId, default to the first chat if ID is invalid/not found
  const currentChat = chats.find((chat) => chat.id === currentChatId) || (chats.length > 0 ? chats[0] : undefined);

  // Function to update chat title based on the first *user* message
  const updateChatTitle = (chatId: string, messages: Message[]) => {
    // Find the first user message to set the title
    const firstUserMessage = messages.find(msg => msg.role === 'user');
    if (!firstUserMessage) return // Don't update title if no user message yet

    const userContent = firstUserMessage.content
    // Generate title: truncate if longer than 20 chars
    const title = userContent.length > 20 ? userContent.substring(0, 20) + "..." : userContent

    // Update the title for the specific chat
    setChats(prevChats =>
      prevChats.map((chat) =>
        chat.id === chatId ? { ...chat, title } : chat
      )
    )
  }

  const sendMessage = async (content: string) => {
    if (!content.trim() || !currentChat) return // Ensure there is a current chat

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content,
      timestamp: new Date().toISOString(),
    }

    // --- Optimistic UI Update ---
    // 1. Prepare the updated messages array including the new user message
    const updatedMessages = [...currentChat.messages, userMessage]

    // 2. Check if this is the first user message to update the title
    const isFirstUserMessage = currentChat.messages.filter(m => m.role === 'user').length === 0;

    // 3. Update the chats state immediately with the user message
    setChats(prevChats =>
      prevChats.map((chat) =>
        chat.id === currentChatId
          ? { ...chat, messages: updatedMessages }
          : chat
      )
    )

    // 4. Update title if needed (after state update with user message)
    if (isFirstUserMessage) {
      // Use a slight delay or useEffect based on messages to ensure state is updated
      // Or update directly here, assuming setChats updates synchronously enough for this logic
      updateChatTitle(currentChatId, updatedMessages);
    }


    // --- API Call ---
    setIsLoading(true)
    try {
      const response = await fetch("/api/chat", { // Your API endpoint
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // Send messages up to the user's latest message for context
          messages: updatedMessages,
          systemPrompt,
          temperature,
        }),
      })

      if (!response.ok) {
        // Throw an error with status text for better debugging
        throw new Error(`API request failed: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(), // Ensure unique ID
        role: "model",
        content: data.text, // Assuming your API returns { text: "..." }
        timestamp: new Date().toISOString(),
      }

      // --- Update State with AI Response ---
      setChats(prevChats =>
        prevChats.map((chat) =>
          chat.id === currentChatId
            ? { ...chat, messages: [...updatedMessages, aiMessage] } // Add AI message
            : chat
        )
      )

    } catch (error) {
      console.error("Error sending message:", error)
      // --- Add Error Message to Chat ---
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "model", // Display as an AI message
        content: `Sorry, I encountered an error. Please try again. (${error instanceof Error ? error.message : 'Unknown error'})`,
        timestamp: new Date().toISOString(),
      }

      setChats(prevChats =>
        prevChats.map((chat) =>
          chat.id === currentChatId
            ? { ...chat, messages: [...updatedMessages, errorMessage] } // Add error message instead of AI response
            : chat
        )
      )
    } finally {
      setIsLoading(false) // Stop loading indicator
    }
  }

  // --- Download Function ---
  const downloadChats = () => {
    if (chats.length === 0) {
      alert("No chat history to download."); // Or use a more integrated notification
      return;
    }
    try {
      // Convert the chats array to a JSON string
      const chatsJson = JSON.stringify(chats, null, 2); // Pretty print JSON
      // Create a Blob object of type JSON
      const blob = new Blob([chatsJson], { type: "application/json" });
      // Create a temporary URL for the Blob
      const url = URL.createObjectURL(blob);
      // Create a temporary anchor element
      const a = document.createElement("a");
      // Set the href to the Blob URL
      a.href = url;
      // Set the download attribute with a filename
      a.download = `chat_history_${new Date().toISOString().split('T')[0]}.json`;
      // Append the anchor to the body (required for Firefox)
      document.body.appendChild(a);
      // Programmatically click the anchor to trigger the download
      a.click();
      // Remove the anchor from the body
      document.body.removeChild(a);
      // Revoke the temporary URL to free up memory
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download chat history:", error);
      alert("Failed to download chat history. See console for details.");
    }
  };

  // --- Render ---
  return (
    <main className="flex h-screen bg-[#f5f5f5]">
      {/* Chat History Sidebar */}
      <ChatHistory
        chats={chats}
        currentChatId={currentChatId}
        setCurrentChatId={setCurrentChatId}
        createNewChat={createNewChat}
        // Pass the download function to ChatHistory
        // You'll need to add a button inside ChatHistory to call this prop
        downloadChats={downloadChats}
      />

      {/* Chat Area - Conditionally render only if a currentChat exists */}
      {currentChat ? (
        <ChatArea
          // Use a key to force re-render when chat changes, helps reset scroll, etc.
          key={currentChat.id}
          messages={currentChat.messages}
          sendMessage={sendMessage}
          isLoading={isLoading}
          openSettings={() => setIsSettingsOpen(true)}
        />
      ) : (
        // Optional: Show a placeholder if no chat is selected or available
        <div className="flex-1 flex items-center justify-center text-gray-500">
          Select a chat or start a new one.
        </div>
      )}


      {/* Settings Modal */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        systemPrompt={systemPrompt}
        setSystemPrompt={setSystemPrompt}
        temperature={temperature}
        setTemperature={setTemperature}
      />

      {/*
        Alternative Download Button Placement:
        If you don't want to modify ChatHistory, you could place a button here.
        Example:
        <button
          onClick={downloadChats}
          className="absolute bottom-4 right-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded shadow"
        >
          Download All Chats
        </button>
      */}
    </main>
  )
}
